import _ from 'lodash'
import {IEvent} from '../contexts/labeling'

export interface ICutEventsResult<T extends IEvent> {
  remainder: T[]
  cutout: T[]
}

const _eventKeys: ['type', 'start', 'end'] = ['type', 'start', 'end']
function _mergeValues<T>(a: T, b: T): T {
  function _customizer(a: T, b: T) {
    if (_.isArray(a) && _.isArray(b)) {
      return _.union(a, b)
    }
  }
  return _.mergeWith(a, b, _customizer)
}

export function findEventOverlap<T extends IEvent>(events: T[], target: T, trim?: boolean): T[] {
  const overlap: T[] = []
  const targetTimes: number[] = _.chain([target.start, target.end])
    .filter(_.isNumber)
    .orderBy()
    .value()
  const targetMin = targetTimes[0]
  let targetMax: number | undefined
  if (targetTimes.length === 2) targetMax = targetTimes[1]

  events.forEach((event) => {
    if (!event) return
    if (!target.type) return
    if (target.type !== event.type) return
    const eventTimes: number[] = _.chain([event.start, event.end])
      .filter(_.isNumber)
      .orderBy()
      .value()
    const eventMin = eventTimes[0]
    let eventMax: number | undefined
    if (eventTimes.length === 2) eventMax = eventTimes[1]
    if (eventMin !== undefined) {
      if (targetMax !== undefined) {
        if (eventMin >= targetMax) return
      }
      // target have no duration
      if (targetMax === undefined) {
        if (targetMin !== undefined && eventMin >= targetMin) return
      }
    }
    if (eventMax !== undefined) {
      if (targetMin !== undefined) {
        if (eventMax <= targetMin) return
      }
    }
    if (trim) {
      const _e = {...event}
      if (eventMin !== undefined && targetMin !== undefined && eventMin < targetMin)
        _e.start = targetMin
      if (eventMax !== undefined && targetMax !== undefined && eventMax > targetMax)
        _e.end = targetMax
      if (targetMax !== undefined) {
        overlap.push(_e)
        return
      }
    }
    overlap.push(event)
  })
  return overlap
}

export function findEventGaps<T extends IEvent>(
  events: T[],
  target: T
): Pick<T, 'type' | 'start' | 'end'>[] {
  if (!target.type) return []
  if (target.end === undefined) return []
  const overlap = findEventOverlap(events, target, false)
  const type = target.type
  const gaps: Pick<T, 'type' | 'start' | 'end'>[] = []
  _.chain(overlap)
    .forEach((e, i) => {
      if (!e) return
      if (target.end === undefined) return

      // gaps before first event?
      if (i === 0) {
        if (target.start < e.start) {
          // fill the gap before first event
          gaps.push({
            type,
            start: target.start,
            end: e.start,
          })
        }
      }

      // gaps after the last event?
      if (i === overlap.length - 1) {
        if (e.end === undefined) {
          gaps.push({
            type,
            start: e.start,
            end: target.end,
          })
        } else {
          if (e.end < target.end) {
            // fills the gap after last event
            gaps.push({
              type,
              start: e.end,
              end: target.end,
            })
          }
        }
      }

      // gaps between two events
      const _prev = overlap[i - 1]
      if (_prev) {
        if (_prev.end !== undefined) {
          if (_prev.end < e.start) {
            // fills gaps between two events
            gaps.push({
              type,
              start: _prev.end,
              end: e.start,
            })
          }
        }
      }
    })
    .value()

  return cleanupEvents(gaps)
}

export function cutEvents<T extends IEvent>(events: T[], target: T): ICutEventsResult<T> {
  if (target.end === undefined)
    return {
      remainder: events,
      cutout: [],
    }
  const cutout: T[] = findEventOverlap(events, target, true)
  const _events: T[] = _.chain(events)
    .map((event) => {
      if (!target.type) return event

      // events of different types, nothing to do here
      if (target.type !== event.type) return event

      // crop target without duration, nothing to do here
      if (target.end === undefined) return event

      // events without duration
      if (event.end === undefined) {
        if (target.start <= event.start && target.end >= event.start) return
        return event
      }

      // split
      if (target.start > event.start && target.end < event.end) {
        return [
          {
            ...event,
            end: target.start,
          },
          {
            ...event,
            start: target.end,
          },
        ]
      }

      // fully contained in cutout
      if (target.start <= event.start && target.end >= event.end) {
        return
      }

      // trim start
      if (target.start <= event.start && target.end > event.start) {
        return [
          {
            ...event,
            start: target.end,
          },
        ]
      }

      // trim end
      if (target.start >= event.start && target.start < event.end) {
        return [
          {
            ...event,
            end: target.start,
          },
        ]
      }

      return event
    })
    .compact()
    .flatten()
    .value() as T[]
  return {
    remainder: _events,
    cutout,
  }
}

export function cleanupEvents<T extends IEvent>(events: T[]): T[] {
  // cleanup, merge identical neighbours etc
  const chains: {[key: string]: T[][]} = {}
  _.chain(_.cloneDeep(events))
    .groupBy((e) => e.type || '')
    .toPairs()
    .map(([k, v]) => {
      if (!k) return
      return [
        k,
        _.chain(v)
          .sortBy((event) => event.start)
          .value(),
      ] as [string, T[]]
    })
    .compact()
    .forEach(([k, v]) => {
      if (!chains[k]) chains[k] = []
      let _chain: T[] = []
      let _prev: T | undefined
      _.forEach(v, (e) => {
        if (!_prev) {
          _chain.push(e)
          _prev = e
          return
        }
        let isNeighbourOrOverlap = false

        // |-------------------|
        //                     ^ min end
        //      |------------------|
        //      ^ max start
        const maxStart = _.max([_prev.start, e.start]) as number
        const minEnd = _.min([_prev.end, e.end])
        const isPayloadIdentical = _.isEqual(_.omit(_prev, _eventKeys), _.omit(e, _eventKeys))

        if (_prev.end !== undefined && minEnd !== undefined) {
          // overlap
          if (maxStart < minEnd) isNeighbourOrOverlap = true
          // neighbour
          else if (maxStart === minEnd && isPayloadIdentical) isNeighbourOrOverlap = true
        }

        // keep or break chain?
        if (isNeighbourOrOverlap) _chain.push(e)
        else {
          chains[k].push(_chain)
          _chain = [e]
        }
        _prev = e
      })
      chains[k].push(_chain)
    })
    .value()

  const reduced: T[] = _.chain(chains)
    .values()
    .map((chains) => {
      return _.chain(chains)
        .map((chain) => {
          return _.reduce(chain, (prev, curr) => {
            return {
              ..._mergeValues(_.cloneDeep(prev), curr),
              start: _.min([prev.start, curr.start]),
              end: _.max([prev.end, curr.end]),
            }
          })
        })
        .flatten()
        .compact()
        .value() as T[]
    })
    .flattenDeep()
    .sortBy((e) => e.start)
    .value()

  return reduced
}

export function mergeEvents<T extends IEvent>(events: T[], target: T): T[] {
  // special case, "global" events (-1 -> -1)
  if (target.start !== undefined && target.end !== undefined) {
    // mixing -1 values and regular timestamps is not allowed
    if ((target.start === -1 || target.end === -1) && (target.start >= 0 || target.end >= 0)) {
      return events
    }
  }

  const _targetCopy = _.cloneDeep(target)
  if (_targetCopy.start === -1) _targetCopy.end = -1
  else {
    // no other negative values are allowed
    const _minVal = _.min([_targetCopy.start || 0, _targetCopy.end || 0])
    if (_minVal === undefined || _minVal < 0) {
      return events
    }
  }

  // if target exactly matches an already existing event then no further merging logic is needed
  const _findExact = _.findIndex(
    events,
    (e) => e.start === _targetCopy.start && e.end === _targetCopy.end
  )
  if (_findExact !== -1) {
    const res = [
      ..._.slice(events, 0, _findExact),
      ..._.slice(events, _findExact + 1, events.length),
      _targetCopy,
    ]
    return cleanupEvents(res)
  }

  const {remainder, cutout} = cutEvents(events, _targetCopy)

  const _remainderCopy = _.cloneDeep(remainder)
  const _cutoutCopy = _.cloneDeep(cutout)
  const _targetPayload = _.omit(_targetCopy, _eventKeys)

  // merge cutout pieces together with target payload
  for (let i = 0; i < _cutoutCopy.length; i++) {
    const curr = _cutoutCopy[i]
    const next = _cutoutCopy[i + 1]
    if (curr.end === undefined) {
      if (next) curr.end = next.start
      else curr.end = target.end
    }
    _mergeValues(curr, _targetPayload)
  }

  // finding all the gaps between events within the target window
  const _targetPieces: T[] = _.chain(findEventGaps(_cutoutCopy, _targetCopy))
    .map(
      (gap) =>
        ({
          ...gap,
          ..._targetPayload,
        }) as T
    )
    .value()

  // no gaps found within target window
  if (_targetPieces.length === 0 && _cutoutCopy.length === 0) {
    _targetPieces.push({
      ..._targetCopy,
      ..._targetPayload,
    })
  }

  // merge the overlaps and the rest of events back together
  const _merged: T[] = _.compact([..._remainderCopy, ..._cutoutCopy, ..._targetPieces])

  // cleanup
  const mergedAndCleaned: T[] = cleanupEvents(_merged)
  return mergedAndCleaned
}
